import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, ShieldCheck, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inp, L, Modal } from "./inputs";

const ROLES = [
  ["financial_admin", "Financial Administrator", "Fees & tax settings, promo codes, orders, refunds, reports, member giving"],
  ["store_manager", "Store Manager", "Products, promo codes, orders & fulfillment — no financial settings or giving records"],
  ["event_manager", "Event Manager", "Events & tickets, check-in roster"],
  ["checkin_staff", "Check-In Staff", "Scan & check in tickets and membership cards only — no prices or financial data"],
  ["membership_admin", "Membership Administrator", "People/members, membership statuses & applications — no giving amounts"],
  ["ministry_leader", "Ministry Leader", "Review ministry applications, card check-in — no financial or giving data"],
];

export const StaffTab = () => {
  const [staff, setStaff] = useState(null);
  const [log, setLog] = useState(null);
  const [adding, setAdding] = useState(null);

  const load = () => {
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => toast.error("Failed to load staff"));
    api.get("/admin/audit-log").then((r) => setLog(r.data)).catch(() => setLog([]));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/admin/staff", adding); toast.success("Staff member added — they can now sign in with Google at /admin/login"); setAdding(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };
  const toggle = async (s) => { try { await api.put(`/admin/staff/${s.id}`, { active: !s.active }); load(); } catch { toast.error("Update failed"); } };
  const del = async (s) => { if (!window.confirm(`Remove ${s.email}?`)) return; try { await api.delete(`/admin/staff/${s.id}`); load(); } catch { toast.error("Remove failed"); } };
  const roleLabel = (r) => ROLES.find((x) => x[0] === r)?.[1] || r;

  return (
    <div data-testid="staff-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-bold text-2xl">Staff & Roles</h1>
        <button onClick={() => setAdding({ email: "", name: "", role: "checkin_staff" })} data-testid="add-staff-btn" className="inline-flex items-center gap-2 rounded-full bg-[#E63946] px-5 py-2.5 text-sm hover:bg-[#BA1B25]"><Plus size={16} /> Add Staff</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        {ROLES.map(([v, l, d]) => (
          <div key={v} className="bg-white/[0.03] border border-white/10 rounded-lg p-4">
            <p className="font-medium text-sm flex items-center gap-2"><ShieldCheck size={14} className="text-[#E63946]" /> {l}</p>
            <p className="text-xs text-white/40 mt-1">{d}</p>
          </div>
        ))}
      </div>
      {!staff ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div> : (
        <div className="space-y-2 mb-10">
          {staff.map((s) => (
            <div key={s.id} data-testid="staff-row" className="flex items-center gap-4 bg-white/[0.03] border border-white/10 rounded-lg p-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{s.name || s.email} {!s.active && <span className="text-xs text-white/30 ml-1">(disabled)</span>}</p>
                <p className="text-sm text-white/40 truncate">{s.email} · {roleLabel(s.role)}</p>
              </div>
              <button onClick={() => toggle(s)} className={`text-xs px-3 py-1.5 rounded-full border ${s.active ? "border-emerald-500 text-emerald-400" : "border-white/15 text-white/50"}`}>{s.active ? "Active" : "Disabled"}</button>
              <button onClick={() => del(s)} className="grid place-items-center w-9 h-9 rounded-full hover:bg-[#E63946]"><Trash2 size={15} /></button>
            </div>
          ))}
          {staff.length === 0 && <p className="text-white/40 py-8 text-center">No staff yet. Only the super administrator can sign in. Add staff by their Google email.</p>}
        </div>
      )}

      <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><ScrollText size={17} className="text-[#E63946]" /> Activity Log</h2>
      {!log ? <Loader2 className="animate-spin text-[#E63946]" /> : (
        <div className="space-y-1.5 max-h-[420px] overflow-y-auto" data-testid="audit-log">
          {log.map((e) => (
            <div key={e.id} className="text-sm bg-white/[0.02] border border-white/5 rounded-md px-3 py-2 flex flex-wrap gap-x-2">
              <span className="text-white/30 shrink-0">{new Date(e.created_at).toLocaleString()}</span>
              <span className="text-white/60">{e.admin}</span>
              <span className="text-[#E63946] font-mono text-xs self-center">{e.action}</span>
              <span className="text-white/50 truncate">{e.target} {e.detail}</span>
            </div>
          ))}
          {log.length === 0 && <p className="text-white/40 py-6 text-center">No activity recorded yet.</p>}
        </div>
      )}

      {adding && (
        <Modal title="Add Staff Member" onClose={() => setAdding(null)} onSave={save} saveLabel="Add">
          <p className="text-sm text-white/50">They will sign in with Google at <span className="font-mono">/admin/login</span> using this exact email, and only see the sections their role allows.</p>
          <div><L>Google Email</L><input className={inp} data-testid="staff-email-input" value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} /></div>
          <div><L>Name</L><input className={inp} value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} /></div>
          <div><L>Role</L>
            <select className={inp} data-testid="staff-role-select" value={adding.role} onChange={(e) => setAdding({ ...adding, role: e.target.value })}>
              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
};
